// models/api/licenses.ts
import _ from 'lodash'
import { getRequest, postRequest, deleteRequest } from './rest'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import type { License } from 'lib/types/license'

const orgHeader = (orgId: string) => (orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId })

// Build a stable id if backend doesn't provide one
const buildId = (l: any) => {
  const raw =
    l?.id ??
    l?.uuid ??
    l?.license_id ??
    // fallback: deterministic composite (org + user + type)
    `${l?.orgId ?? l?.org_id ?? 'no-org'}:${l?.userId ?? l?.user_id ?? 'no-user'}:${l?.type ?? 'no-type'}`
  return String(raw)
}

const normalize = (l: any): License => {
  const raw = l?.license ?? l // tolerant of {license:{...}} or plain object
  return {
    ...raw,
    id: buildId(raw),
    orgId: raw?.orgId ?? raw?.org_id ?? '',
    userId: raw?.userId ?? raw?.user_id ?? null,
    alertSourceId: raw?.alertSourceId ?? raw?.alert_source_id ?? null,
    subjectName: raw?.subjectName ?? raw?.subject_name ?? null,
    type: raw?.type ?? 'standalone_voice',
    insertedAt: raw?.insertedAt ?? raw?.inserted_at,
    updatedAt: raw?.updatedAt ?? raw?.updated_at,
  } as License
}

// v5 pages at 50 by default; one page this size covers an org's licenses.
const LICENSES_LIMIT = 1000

// Only standalone licenses: integrated_voice licenses belong to voice
// buttons and aren't user-assignable seats.
export const getLicenses = async (orgId: string): Promise<License[]> => {
  const qs = [`limit=${LICENSES_LIMIT}`, 'filter[type]=standalone_voice']
  const res = await getRequest({
    endpoint: `api/v5/licenses?${qs.join('&')}`,
    header: orgHeader(orgId),
  })

  // Your example shows { data: [...], meta: {...} } (no nested res.data.data)
  const list = res?.data?.data ?? res?.data ?? []
  return (list || []).map((l: any) => normalize({ ...l, orgId }))
}

export type VoiceLicensedUser = {
  userId: string
  // The user's email from the index's `subjectName`, else the user id.
  label: string
}

export type VoiceLicenses = {
  // Alert sources holding a voice license of any type (integrated or standalone).
  alertSourceIds: Set<string>
  // Users holding a standalone license. A user's voice follows them onto
  // whichever phone they sign in to, and the API doesn't record which phone
  // that is, so these can't be matched to alert sources.
  users: VoiceLicensedUser[]
}

export const getVoiceLicenses = async (orgId: string): Promise<VoiceLicenses> => {
  const res = await getRequest({
    endpoint: `api/v5/licenses?limit=${LICENSES_LIMIT}`,
    header: orgHeader(orgId),
  })
  const list: License[] = (res?.data?.data ?? res?.data ?? []).map(normalize)

  const alertSourceIds = new Set(list.map(l => l.alertSourceId).filter((id): id is string => Boolean(id)))
  const users = _.uniqBy(
    list.filter(l => l.userId).map(l => ({ userId: String(l.userId), label: l.subjectName || String(l.userId) })),
    'userId',
  ).sort((a, b) => a.label.localeCompare(b.label))

  return { alertSourceIds, users }
}

export const createLicense = async (orgId: string, userId: string): Promise<License> => {
  const res = await postRequest({
    endpoint: 'api/v5/licenses',
    header: orgHeader(orgId),
    data: { orgId, userId },
  })

  // tolerate either {data:{...}} or plain object
  const payload = res?.data?.data ?? res?.data
  return normalize(payload)
}

// DELETE /api/v5/licenses/{id} -> usually 204
export const deleteLicense = async (licenseId: string, orgId?: string): Promise<void> => {
  await deleteRequest({
    endpoint: `api/v5/licenses/${licenseId}`,
    header: orgId ? orgHeader(orgId) : {},
  })
}
